/**
 * Utilitário para gerar URLs otimizadas de imagens do Supabase Storage
 * 
 * Combina redimensionamento e otimização de formato em uma única chamada:
 * - width: Reduz a resolução (dinâmico conforme necessário)
 * - resize=contain: Redimensiona sem cortar, mantendo a proporção
 * - quality=80: Compressão inteligente
 * - O Supabase converte automaticamente para WebP quando suportado pelo navegador
 * 
 * @param url - URL pública original do Supabase ou URL completa
 * @param width - Largura desejada da imagem em pixels (opcional)
 * @returns URL otimizada com render/image
 * 
 * @example
 * // Para um avatar de 40px
 * getOptimizedImageUrl('https://xyz.supabase.co/storage/v1/object/public/avatars/user.jpg', 40)
 * // Retorna: https://xyz.supabase.co/storage/v1/render/image/public/avatars/user.jpg?width=40&resize=contain&quality=80
 */
export function getOptimizedImageUrl(url: string | null | undefined, width?: number): string | undefined {
  if (!url) return undefined;

  try {
    // Se a URL já é uma URL renderizada, retornar como está (evitar duplicação)
    if (url.includes('/render/image/')) {
      return url;
    }

    // Parsear a URL para lidar corretamente com query strings
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(url);
    } catch {
      // Se não for uma URL válida, retornar como está
      return url;
    }

    // Verificar se é uma URL do Supabase Storage
    const hostname = parsedUrl.hostname;
    if (!hostname.includes('.supabase.co')) {
      // Se não for uma URL do Supabase, retornar a URL original
      return url;
    }

    // Extrair informações do caminho
    // Padrão: /storage/v1/object/public/[BUCKET]/[PATH]
    const pathMatch = parsedUrl.pathname.match(/^\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
    
    if (!pathMatch) {
      // Se não corresponder ao padrão esperado, retornar a URL original
      return url;
    }

    const [, bucket, path] = pathMatch;
    
    // Construir URL otimizada
    // Remover query strings da URL original ao construir a nova
    const baseUrlFull = `${parsedUrl.protocol}//${hostname}`;
    const optimizedPath = `/storage/v1/render/image/public/${bucket}/${path}`;
    
    // Construir URL com parâmetros (sem os query params originais)
    const optimizedUrl = new URL(optimizedPath, baseUrlFull);
    
    // Adicionar parâmetros de otimização
    if (width && width > 0) {
      optimizedUrl.searchParams.set('width', width.toString());
      // Usar 'contain' para redimensionar sem cortar (mantém proporção)
      // 'cover' (padrão) corta a imagem, 'contain' ajusta dentro das dimensões
      optimizedUrl.searchParams.set('resize', 'contain');
    }
    optimizedUrl.searchParams.set('quality', '80');
    // O Supabase converte automaticamente para WebP quando suportado pelo navegador
    // Não é necessário especificar o formato explicitamente

    const optimized = optimizedUrl.toString();
    
    return optimized;
  } catch (error) {
    // Em caso de erro, retornar a URL original
    return url;
  }
}

/**
 * Gera URLs responsivas com diferentes tamanhos para srcset
 * 
 * @param url - URL pública original do Supabase
 * @param baseWidth - Largura base em pixels
 * @param devicePixelRatio - Device pixel ratio (padrão: window.devicePixelRatio ou 1)
 * @returns Objeto com src e srcset para uso em <img>
 */
export function getResponsiveImageUrl(
  url: string | null | undefined, 
  baseWidth: number,
  devicePixelRatio: number = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
): { src: string | undefined; srcset?: string } {
  if (!url) return { src: undefined };

  // Calcular width considerando device pixel ratio (para telas retina)
  const actualWidth = Math.ceil(baseWidth * devicePixelRatio);
  
  // Gerar URL principal (sempre retornar a URL original se a otimização falhar)
  const optimizedSrc = getOptimizedImageUrl(url, actualWidth);
  const src = optimizedSrc || url;
  
  // Criar srcset com diferentes tamanhos para responsividade
  // Apenas se a URL for do Supabase (para evitar srcset com URLs externas)
  const isSupabaseUrl = url.includes('.supabase.co/storage/v1/object/public/');
  
  if (isSupabaseUrl) {
    const widths = [
      Math.ceil(baseWidth * 0.5), // 0.5x
      baseWidth,                   // 1x
      Math.ceil(baseWidth * 1.5),  // 1.5x
      baseWidth * 2,                // 2x (retina)
      baseWidth * 3,                // 3x (super retina)
    ].filter(w => w > 0 && w <= 2048); // Limitar tamanho máximo

    const srcset = widths
      .map(w => {
        const optimized = getOptimizedImageUrl(url, w);
        return optimized ? `${optimized} ${w}w` : null;
      })
      .filter(Boolean)
      .join(', ');

    return {
      src,
      srcset: srcset || undefined,
    };
  }

  // Se não for URL do Supabase, retornar apenas src
  return { src };
}

/**
 * Extrai o tamanho do width de uma classe CSS do Tailwind (ex: h-10 w-10 -> 40px)
 * 
 * @param className - String com classes CSS do Tailwind
 * @returns Largura em pixels ou undefined
 */
export function extractWidthFromClassName(className?: string): number | undefined {
  if (!className) return undefined;

  // Padrões do Tailwind: w-{size}, h-{size}
  // Tamanhos comuns: 4=16px, 5=20px, 6=24px, 8=32px, 10=40px, 12=48px, 16=64px, 20=80px
  const sizeMap: Record<string, number> = {
    '4': 16,
    '5': 20,
    '6': 24,
    '8': 32,
    '10': 40,
    '12': 48,
    '16': 64,
    '20': 80,
    '24': 96,
  };

  // Procurar por w-{size} ou h-{size}
  const widthMatch = className.match(/\b[wh]-(\d+)\b/);
  if (widthMatch) {
    const size = widthMatch[1];
    return sizeMap[size] || parseInt(size) * 4; // Fallback: multiplicar por 4 (padrão Tailwind)
  }

  return undefined;
}
